import { HeaderActions } from "components/AppLayout/HeaderActions";
import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Padlock } from "components/icons/Padlock";
import Table from "components/DesignSystem/Table/V2/Table";
import { SUMMARY_REPORT, SUMMARY_REPORT_TYPES } from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import * as DATE_PERIOD from "constants/dateFilter";
import { PERIOD } from "constants/revenueMetrics";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AnimatePresence, motion } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useElementDimensions } from "hooks/useElementDimensions";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { parse, stringify } from "qs";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Header } from "components/DesignSystem/Header/Header";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  RowSelectionState,
  useReactTable,
} from "react-table-8.10.7";
import EmptyCalender from "static/images/EmptyCalendar.svg";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  useGetTransactionsForReportQuery,
  useGetVendorSummaryQuery,
  useLazyExportVendorSummaryQuery,
  VendorSummaryItem,
  VendorSummaryPeriod,
} from "store/apis/reports";
import { GeneralLedgerTable } from "components/GeneralLedger/GeneralLedgerAccordion/GeneralLedgerAccordion";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { TransactionSlider } from "components/Transaction/Slider/TransactionSlider";
import { BackendError } from "types/utils/error";
import { getColumnWidthStyle } from "components/GeneralLedger/TrialBalanceView/TrialBalanceView";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { getPinnedShadowClass } from "utils/getPinnedShadowClass";
import { getDateRange } from "utils/getDateRange";
import { AddToClosing as AddToClosingModal } from "../AddToClosing";
import {
  FilterValues,
  VendorFilter,
  ViewByDropdown,
} from "./VendorSummaryFilter";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { ArrowRight } from "components/icons/ArrowRight";
import { RelatedPartyIcon } from "components/icons/RelatedPartyIcon";
import { ExportVendorSummary } from "../ExportVendorSummary";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { AccountingMethodBanner } from "components/BooksSettings/AccountingMethodBanner";
import { TableSelectionControls } from "components/GeneralLedger/TableSelectionControls";
import { usePageTitle } from "hooks/usePageTitle";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { stopPropagation } from "utils/stopPropagation";
import { AddCommentAnchor } from "components/AddCommentPopoverAnchor/AddCommentPopoverAnchor";
import Popover from "components/DesignSystem/Popover/Popover";
import { AddCommentPop } from "components/AddComment/AddComment";
import { ChatTeardropDots } from "components/icons/ChatTeardropDots";
import { CommentsSlider } from "components/CommentsSlider/CommentsSlider";
import { SliderAnimation } from "components/SliderAnimation/SliderAnimation";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { setOpenComment } from "store/slices/openComment";
import { COMMENT_TYPE, NoteResponse } from "store/apis/notes";
import ToolTip from "components/design/toolTip";
import Loader from "components/DesignSystem/Loader/loader";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { DEFAULT_PAGE_SIZE } from "constants/base";

dayjs.extend(relativeTime);

export type VendorSummaryRow = {
  id: string;
  categoryId?: string;
  vendorId?: string;
  commentAnchorId?: string;
  name: string;
  isRelatedParty?: boolean;
  period: VendorSummaryPeriod;
  children?: VendorSummaryRow[];
};

const getChildList = (
  item: VendorSummaryItem,
  parentIndex: number
): VendorSummaryRow[] => {
  if ("vendor_list" in item) {
    return item.vendor_list.map((child, childIndex) => ({
      id: `${parentIndex}-${childIndex}`,
      categoryId: item.category_id,
      vendorId: child.vendor_id,
      name: child.name,
      period: child.period,
    }));
  }

  if ("category_list" in item) {
    return item.category_list.map((child, childIndex) => ({
      id: `${parentIndex}-${childIndex}`,
      categoryId: child.category_id,
      vendorId: item.vendor_id,
      commentAnchorId: child.category_id,
      name: child.name,
      period: child.period,
    }));
  }

  return [];
};

const buildTableData = (
  data: VendorSummaryItem[],
  isGroupByCategory: boolean
): VendorSummaryRow[] =>
  data.map((item, index) => {
    const children = getChildList(item, index);
    const parentPeriod = mergePeriods(children.map((c) => c.period));
    const categoryId =
      isGroupByCategory && "category_id" in item ? item.category_id : undefined;

    return {
      id: String(index),
      categoryId,
      commentAnchorId: categoryId,
      name: item.name,
      isRelatedParty:
        "is_related_party" in item ? item.is_related_party : undefined,
      period: parentPeriod,
      children,
    };
  });

const mergePeriods = (periods: VendorSummaryPeriod[]): VendorSummaryPeriod => {
  const merged: VendorSummaryPeriod = {};

  for (const period of periods) {
    for (const key of Object.keys(period)) {
      const value = period[key];
      if (!value) continue;

      if (!merged[key]) {
        merged[key] = { ...value, amount: 0 };
      }
      merged[key] = {
        ...merged[key]!,
        amount: merged[key]!.amount + value.amount,
      };
    }
  }

  return merged;
};

const COLUMN_IDS = {
  NAME: "name",
  ACTIONS: "actions",
} as const;

const FIXED_WIDTH_COLUMNS: string[] = [COLUMN_IDS.ACTIONS];

const columnHelper = createColumnHelper<VendorSummaryRow>();

const nameColumn = columnHelper.accessor("name", {
  id: COLUMN_IDS.NAME,
  header: "",
  cell: ({ row, getValue }) => {
    const name = getValue();
    const isParent = row.depth === 0;
    const { isRelatedParty } = row.original;

    return (
      <div className="t-flex t-items-center t-gap-2">
        {isParent && (
          <div
            className={classNames(
              "t-transform t-transition t-duration-300 t-ease-in-out t-flex-shrink-0 t-text-text-30",
              {
                "t-rotate-90": row.getIsExpanded(),
                "t-invisible": !row.getCanExpand(),
              }
            )}
          >
            <ArrowRight stroke="1.5" color="currentColor" />
          </div>
        )}
        <span
          className={classNames("t-text-body-sm t-truncate", {
            "t-pl-8": !isParent,
          })}
        >
          {name}
        </span>
        {isRelatedParty && (
          <ToolTip text="Related party">
            <div className="t-flex t-items-center">
              <RelatedPartyIcon />
            </div>
          </ToolTip>
        )}
      </div>
    );
  },
});

const getActionsColumn = (hasWritePermission: boolean) =>
  columnHelper.display({
    id: COLUMN_IDS.ACTIONS,
    size: 55,
    header: () => null,
    cell: (info) => {
      const { categoryId, commentAnchorId } = info.row.original;

      if (!categoryId || !commentAnchorId) return <div className="t-w-8"></div>;

      if (!hasWritePermission) {
        return (
          <div className="t-pointer-events-none">
            <Button customType="ghost_icon" size="small" disabled>
              <Padlock />
            </Button>
          </div>
        );
      }

      return (
        <AddCommentPopoverRoot contentId={commentAnchorId}>
          <Dropdown.Root>
            <Dropdown.Trigger asChild onClick={stopPropagation}>
              <div>
                <AddCommentAnchor contentId={commentAnchorId}>
                  <Button customType="ghost_icon" size="small">
                    <img
                      src={ThreeDots}
                      alt="Action"
                      className="t-select-none"
                    />
                  </Button>
                </AddCommentAnchor>
              </div>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content onClick={stopPropagation} align="end">
                <Popover.Trigger asChild>
                  <Dropdown.Item>Add comment</Dropdown.Item>
                </Popover.Trigger>
              </Dropdown.Content>
            </Dropdown.Portal>
            <Popover.Portal>
              <AddCommentPop
                commentType={COMMENT_TYPE.VENDOR_SUMMARY_COMMENT}
                contentId={categoryId}
              />
            </Popover.Portal>
          </Dropdown.Root>
        </AddCommentPopoverRoot>
      );
    },
  });

const getAmountColumns = (
  periodKeys: string[],
  isTotalView: boolean,
  onAmountClick: (rowId: string, colId: string) => void,
  expandedCell: { rowId: string; colId: string } | null
) =>
  periodKeys.map((key) =>
    columnHelper.accessor((row) => row.period[key], {
      id: `period_${key}`,
      header: () => <div className="t-flex t-justify-end">{key}</div>,
      size: 130,
      cell: ({ row }) => {
        const periodValue = row.original.period[key];
        const isLeaf = row.depth === 1;
        const { categoryId, vendorId } = row.original;
        const canClick =
          isLeaf &&
          Boolean(categoryId || vendorId) &&
          Boolean(periodValue?.amount);
        const colId = `period_${key}`;
        const isActive =
          canClick &&
          expandedCell?.rowId === row.id &&
          expandedCell?.colId === colId;

        if (!periodValue) {
          return (
            <div className="t-text-subtext-sm t-flex t-justify-end">-</div>
          );
        }

        return (
          <button
            className={classNames(
              "all:unset t-flex t-justify-end t-text-body-sm t-w-full",
              {
                "hover:t-text-purple hover:t-underline t-cursor-pointer":
                  canClick,
                "!t-underline t-text-purple": isActive,
              }
            )}
            onClick={
              canClick
                ? (e) => {
                    e.stopPropagation();
                    onAmountClick(row.id, colId);
                  }
                : undefined
            }
          >
            <AmountSuperScript amount={periodValue.amount} />
          </button>
        );
      },
    })
  );

const AddToClosing = ({
  filters,
}: {
  filters: {
    startDate: string;
    endDate: string;
    reportType: "MONTHLY" | "YEARLY" | "QUARTERLY" | "TOTALS";
    vendorNames: string[];
    accountingMethod: "ACCRUAL" | "CASH";
    comparisonSummaryType: SUMMARY_REPORT_TYPES;
    categoryList: string[];
    isRelatedParty?: boolean;
  };
}) => {
  const { isCpa, isForeignCA } = useRoleBasedView();
  const { alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const addToClosingModal = useModal();

  const [exportVendorSummary, { isFetching: isExporting, data }] =
    useLazyExportVendorSummaryQuery();

  const addToClosing = async () => {
    try {
      await exportVendorSummary({
        entityId,
        ...filters,
      }).unwrap();

      addToClosingModal.open();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  if (isCpa || isForeignCA) {
    return (
      <>
        <Button
          size="xsmall"
          onClick={addToClosing}
          disabled={isExporting}
          isLoading={isExporting}
        >
          Add to closing
        </Button>
        {data?.document_id && (
          <AddToClosingModal
            {...addToClosingModal}
            startDate={filters.startDate}
            endDate={filters.endDate}
            key={
              filters.startDate && filters.endDate
                ? filters.startDate.concat(filters.endDate)
                : ""
            }
            reportFileId={data?.document_id}
            reportType="VENDOR_SUMMARY"
          />
        )}
      </>
    );
  }

  return null;
};

const VendorSummaryTransactions = ({
  categoryId,
  merchantId,
  startDate,
  endDate,
  accountingMethod,
  entityId,
  selectedRows,
  setSelectedRows,
}: {
  categoryId?: string;
  merchantId?: string;
  startDate: string;
  endDate: string;
  accountingMethod: string;
  entityId: string;
  selectedRows: RowSelectionState;
  setSelectedRows: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const query = useQuery();
  const pageSize = Number(query.get("page_size")) || DEFAULT_PAGE_SIZE;
  const [pageNum, setPageNum] = useState(1);

  useEffect(() => {
    setPageNum(1);
  }, [pageSize, categoryId, merchantId, startDate, endDate, accountingMethod]);

  const { data, isLoading, isSuccess, isFetching } =
    useGetTransactionsForReportQuery(
      {
        entityId,
        query: {
          category_id: categoryId!,
          merchant_id: merchantId,
          start_date: startDate,
          end_date: endDate,
          accounting_method: accountingMethod,
          page_num: pageNum,
          page_size: pageSize,
        },
      },
      { skip: !categoryId }
    );

  const transactions = data?.transactions ?? [];

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isEmpty={transactions.length === 0}
      loaderType="secondary"
      customLoader={
        <div className="t-flex t-items-center t-justify-center t-h-14 t-w-full">
          <Loader customType="secondary" />
        </div>
      }
    >
      <Async.Empty>
        <div className="t-flex t-justify-center t-py-3 t-text-body-sm t-text-text-30">
          No transactions found
        </div>
      </Async.Empty>
      <Async.Success>
        <div className="t-flex t-justify-end t-items-center t-pb-2">
          <PaginationHandler
            page_num={data?.current_page || 1}
            per_page={data?.per_page || pageSize}
            total_count={data?.total_count || 0}
            total_pages={data?.total_pages || 1}
            onPageNumChange={setPageNum}
            isLoading={isFetching}
            showOptions
          />
        </div>
        <GeneralLedgerTable
          tableData={transactions}
          reportCategoryType={data?.category_type ?? "STANDARD"}
          startDate={startDate}
          endDate={endDate}
          totalDebits={data?.total_debits ?? 0}
          totalCredits={data?.total_credits ?? 0}
          entityId={entityId}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          hasWritePermission={hasWritePermission}
        />
      </Async.Success>
    </Async.Root>
  );
};

const VendorSummaryTableRow = ({
  row,
  isTotalView,
  isHighlighted,
}: {
  row: Row<VendorSummaryRow>;
  isTotalView: boolean;
  isHighlighted: boolean;
}) => {
  const canExpand = row.getCanExpand();
  const isParent = row.depth === 0;
  const isLeaf = row.depth === 1;

  return (
    <motion.tr
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      layout
      className={classNames(
        "t-border t-border-solid t-border-x-0 t-border-t-0 t-border-neutral-0 t-group t-px-3 t-text-body-sm",
        {
          "t-text-text-100": isParent,
          "t-text-text-60": isLeaf,
          "t-cursor-pointer hover:t-bg-surface-lighter-grey": canExpand,
          "animate-transaction-row": isHighlighted,
        }
      )}
      onClick={() => {
        if (canExpand) row.toggleExpanded();
      }}
    >
      {row.getVisibleCells().map((cell) => {
        const isPinned = cell.column.getIsPinned();
        const pinningStyles = !isTotalView
          ? getCommonPinningStyles(cell.column)
          : {};
        const { boxShadow } = !isTotalView
          ? getPinnedShadowClass(cell.column)
          : { boxShadow: undefined };

        const isFixedWidthColumn =
          isTotalView && FIXED_WIDTH_COLUMNS.includes(cell.column.id);

        const sizeStyle = getColumnWidthStyle({
          size: cell.column.getSize(),
          isGroupedByMonth: !isTotalView,
          isPinnedColumn: isPinned,
          isFixedWidthColumn,
        });

        return (
          <Table.Cell
            key={cell.id}
            style={{ ...pinningStyles, ...sizeStyle }}
            className={classNames(boxShadow, {
              "!t-font-semibold": isParent,
              "!t-z-[3]": isPinned,
              "group-hover:!t-bg-surface-lighter-grey": isPinned && canExpand,
            })}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Table.Cell>
        );
      })}
    </motion.tr>
  );
};

const VendorSummaryExpandedRow = ({
  row,
  isTotalView,
  startDate,
  endDate,
  accountingMethod,
  entityId,
  expandedColId,
  leftPinWidth,
  rightPinWidth,
  selectedRows,
  setSelectedRows,
}: {
  row: Row<VendorSummaryRow>;
  isTotalView: boolean;
  startDate: string;
  endDate: string;
  accountingMethod: string;
  entityId: string;
  expandedColId: string;
  leftPinWidth: number;
  rightPinWidth: number;
  selectedRows: RowSelectionState;
  setSelectedRows: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const [ref, { height: expandedRowHeight }] = useElementDimensions();

  const { categoryId, vendorId } = row.original;

  const periodKey = expandedColId.replace("period_", "");
  const periodData = row.original.period[periodKey];
  const txnStartDate = isTotalView
    ? startDate
    : periodData?.start_date || startDate;
  const txnEndDate = isTotalView ? endDate : periodData?.end_date || endDate;

  if (isTotalView) {
    return (
      <motion.tr
        key={`${row.id}-expanded-${expandedColId}`}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <Table.Cell colSpan={row.getVisibleCells().length}>
          <VendorSummaryTransactions
            categoryId={categoryId}
            merchantId={vendorId}
            startDate={txnStartDate}
            endDate={txnEndDate}
            accountingMethod={accountingMethod}
            entityId={entityId}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </Table.Cell>
      </motion.tr>
    );
  }

  return (
    <motion.tr
      key={`${row.id}-expanded-${expandedColId}`}
      initial={{ height: expandedRowHeight, opacity: 0 }}
      animate={{ height: expandedRowHeight, opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.05 }}
      className="t-w-full t-z-filter-head t-overscroll-x-none"
    >
      <td
        className="t-absolute t-w-full"
        height={expandedRowHeight}
        colSpan={row.getVisibleCells().length}
        style={{
          paddingLeft: `${leftPinWidth}px`,
          paddingRight: `${rightPinWidth}px`,
        }}
      >
        <div
          className="t-absolute t-top-0 t-left-0 t-h-full t-drop-shadow-table-left-pin t-bg-white t-z-[3]"
          style={{ width: `${leftPinWidth}px` }}
        />
        <div
          className="t-px-4 t-py-4 t-w-full t-border-0 t-border-l-2 t-border-solid t-border-neutral-20"
          ref={ref}
        >
          <VendorSummaryTransactions
            categoryId={categoryId}
            merchantId={vendorId}
            startDate={txnStartDate}
            endDate={txnEndDate}
            accountingMethod={accountingMethod}
            entityId={entityId}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </div>
        <div
          className="t-absolute t-top-0 t-right-0 t-h-full t-drop-shadow-table-right-pin t-bg-white t-z-[3]"
          style={{ width: `${rightPinWidth}px` }}
        />
      </td>
    </motion.tr>
  );
};

const getVendorParentTree = (
  rows: VendorSummaryRow[],
  targetId: string,
  parents: string[] = []
): string[] | undefined => {
  for (const row of rows) {
    if (row.commentAnchorId === targetId) return parents;
    if (row.children) {
      const found = getVendorParentTree(row.children, targetId, [
        ...parents,
        row.id,
      ]);
      if (found) return found;
    }
  }
};

export const VendorSummary = () => {
  const { isMobile } = useWindowSize();
  usePageTitle("Vendor Summary");
  const entityId = useCurrentEntityId();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );
  const { startDate, endDate } = getDateRange(DATE_PERIOD.CURRENT_YEAR);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [expandedCell, setExpandedCell] = useState<{
    rowId: string;
    colId: string;
  } | null>(null);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const commentPane = useModal();
  const openComment = useAppSelector((store) => store.openComment.openComment);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { link } = useConstructInternalLink();
  const othersUrl = link("/books/live-financials/others");
  const query = useQuery();
  const selectedCategoryId = query.get("selected_category_id");
  const openCommentParam = query.get("open_comment");

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.category) {
      dispatch(setOpenComment(comment.category.uuid));
    }
  };

  const { data: settings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const { values: filterValues, updateFilter } = useFilters<FilterValues>({
    initialValue: {
      START_DATE: dayjs(startDate).format(YYYY_MM_DD),
      END_DATE: dayjs(endDate).format(YYYY_MM_DD),
      SELECT_PERIOD: DATE_PERIOD.CURRENT_YEAR,
      ACCOUNTING_METHOD: settings?.accounting_method!,
      VENDORS: [],
      CATEGORY: [],
      VIEW_BY: PERIOD.TOTALS,
      COMPARISON_SUMMARY_TYPE: SUMMARY_REPORT.VENDOR,
      RELATED_PARTY: false,
    },
    useQueryParams: true,
  });

  const isTotalView = filterValues.VIEW_BY === PERIOD.TOTALS;
  const isGroupByCategory =
    filterValues.COMPARISON_SUMMARY_TYPE === SUMMARY_REPORT.CATEGORY;

  const filters = useMemo(() => {
    return {
      startDate: dayjs(filterValues.START_DATE).format(YYYY_MM_DD),
      endDate: dayjs(filterValues.END_DATE).format(YYYY_MM_DD),
      reportType: filterValues.VIEW_BY,
      vendorNames: filterValues.VENDORS,
      accountingMethod: filterValues.ACCOUNTING_METHOD,
      categoryList: filterValues.CATEGORY,
      comparisonSummaryType: filterValues.COMPARISON_SUMMARY_TYPE,
      isRelatedParty: filterValues.RELATED_PARTY || undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterValues)]);

  const {
    data: summaryData = [],
    isFetching,
    isSuccess,
  } = useGetVendorSummaryQuery({ entityId, ...filters }, { skip: !entityId });

  const tableData = useMemo(
    () => buildTableData(summaryData, isGroupByCategory),
    [summaryData, isGroupByCategory]
  );

  useEffect(() => {
    if (!openComment) return;
    const parents = getVendorParentTree(tableData, openComment);
    if (parents && parents.length > 0) {
      setExpanded((prev) =>
        typeof prev === "object"
          ? parents.reduce((acc, id) => ({ ...acc, [id]: true }), prev || {})
          : prev
      );
    }
  }, [openComment, tableData]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (selectedCategoryId) {
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.selected_category_id;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [selectedCategoryId]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (openCommentParam) {
      dispatch(setOpenComment(openCommentParam));
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.open_comment;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [openCommentParam]);

  const periodKeys = useMemo(() => {
    const firstChild = tableData[0]?.children?.[0] || tableData[0];
    if (!firstChild) return [];
    return Object.keys(firstChild.period);
  }, [tableData]);

  const onAmountClick = useCallback((rowId: string, colId: string) => {
    setExpandedCell((prev) =>
      prev?.rowId === rowId && prev?.colId === colId ? null : { rowId, colId }
    );
  }, []);

  const amountColumns = useMemo(
    () =>
      getAmountColumns(periodKeys, isTotalView, onAmountClick, expandedCell),
    [periodKeys, isTotalView, onAmountClick, expandedCell]
  );

  const columns = useMemo(
    () => [
      {
        ...nameColumn,
        size: isTotalView ? undefined : 300,
      },
      ...amountColumns,
      getActionsColumn(hasWritePermission),
    ],
    [isTotalView, amountColumns, hasWritePermission]
  );

  const columnPinning =
    isTotalView || isMobile
      ? {}
      : { left: [COLUMN_IDS.NAME], right: [COLUMN_IDS.ACTIONS] };

  const table = useReactTable({
    data: tableData,
    columns,
    state: { expanded, columnPinning },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.children,
    getRowId: (row) => row.id,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const exportAndComments = (
    <>
      <ExportVendorSummary filters={filters} />
      <Button customType="icon" onClick={commentPane.toggle} size="xsmall">
        <ChatTeardropDots />
      </Button>
    </>
  );

  return (
    <>
      <div className="t-flex t-h-full sm:t-h-auto">
        <DashboardContainer
          className={classNames(
            "t-h-full t-w-full t-flex t-pl-5 t-gap-6 t-overflow-x-auto sm:t-h-auto sm:t-overflow-x-visible sm:!t-pl-3 sm:!t-pr-3",
            {
              "t-pr-3": commentPane.isOpen,
              "t-pr-10": !commentPane.isOpen,
            }
          )}
        >
          <DashboardContainer.Header className="t-sticky t-top-0 t-bg-white sm:t-static">
            <Header
              v2
              title={
                <span className="t-font-medium t-text-text-60">
                  Vendor Summary
                </span>
              }
              breadcrumbs={[{ name: "Report List", link: othersUrl }]}
            />
            <div className="t-flex t-flex-col t-gap-4">
              <div className="t-flex t-justify-between sm:t-flex-wrap sm:t-gap-2">
                <div className="t-flex t-gap-2 t-items-center sm:t-flex-wrap">
                  <ViewByDropdown
                    filterValues={filterValues}
                    updateFilter={updateFilter}
                  />
                  <div className="t-border-0 t-border-solid t-border-neutral-10 t-h-6 t-border-l sm:t-hidden" />
                  <VendorFilter
                    filterValues={filterValues}
                    updateFilter={updateFilter}
                  />
                </div>
                <div className="t-justify-end t-flex t-gap-2 t-items-center">
                  <AddToClosing filters={filters} />
                  {!isMobile && exportAndComments}
                </div>
                {isMobile && <HeaderActions>{exportAndComments}</HeaderActions>}
              </div>
              <AccountingMethodBanner
                selectedAccountingMethod={filterValues.ACCOUNTING_METHOD}
                entityId={entityId}
              />
            </div>
          </DashboardContainer.Header>
          <DashboardContainer.Content className="t-overflow-hidden t-pb-28 scrollbar-visible sm:t-overflow-visible">
            <Async.Root
              isLoading={isFetching}
              isSuccess={isSuccess}
              isEmpty={tableData.length === 0}
            >
              <Async.Empty>
                <EmptyScreen
                  className="!t-h-full"
                  text={
                    <span className="t-text-subtext-sm t-text-text-100">
                      No data found
                    </span>
                  }
                >
                  <img src={EmptyCalender} alt="EmptyCalender" />
                </EmptyScreen>
              </Async.Empty>
              <Async.Success>
                <div className="t-relative">
                  <Table.Container className="scrollbar-visible t-h-full t-w-full t-overflow-scroll t-rounded-tl-lg t-rounded-tr-lg">
                    <Table.Content className="t-w-full">
                      <Table.Head>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <Table.HeadRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                              const pinningStyles = !isTotalView
                                ? getCommonPinningStyles(header.column)
                                : {};
                              const { boxShadow } = !isTotalView
                                ? getPinnedShadowClass(header.column)
                                : { boxShadow: undefined };
                              const isPinned = header.column.getIsPinned();
                              const isFixedWidthColumn =
                                isTotalView &&
                                FIXED_WIDTH_COLUMNS.includes(header.column.id);
                              const sizeStyle = getColumnWidthStyle({
                                size: header.getSize(),
                                isGroupedByMonth: !isTotalView,
                                isPinnedColumn: isPinned,
                                isFixedWidthColumn,
                              });

                              return (
                                <Table.HeadCell
                                  key={header.id}
                                  style={{ ...pinningStyles, ...sizeStyle }}
                                  className={classNames(boxShadow, {
                                    "!t-bg-surface-lighter-grey": isPinned,
                                    "!t-z-[3]": isPinned,
                                  })}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </Table.HeadCell>
                              );
                            })}
                          </Table.HeadRow>
                        ))}
                      </Table.Head>
                      <Table.Body>
                        <AnimatePresence initial={false}>
                          {table.getRowModel().rows.map((row) => (
                            <Fragment key={row.id}>
                              <VendorSummaryTableRow
                                row={row}
                                isTotalView={isTotalView}
                                isHighlighted={Boolean(
                                  row.original.commentAnchorId &&
                                    openComment === row.original.commentAnchorId
                                )}
                              />
                              <AnimatePresence mode="wait">
                                {expandedCell?.rowId === row.id && (
                                  <VendorSummaryExpandedRow
                                    row={row}
                                    isTotalView={isTotalView}
                                    startDate={filters.startDate}
                                    endDate={filters.endDate}
                                    accountingMethod={filters.accountingMethod}
                                    entityId={entityId}
                                    expandedColId={expandedCell.colId}
                                    leftPinWidth={
                                      table
                                        .getColumn(COLUMN_IDS.NAME)
                                        ?.getSize() ?? 300
                                    }
                                    rightPinWidth={56}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                  />
                                )}
                              </AnimatePresence>
                            </Fragment>
                          ))}
                        </AnimatePresence>
                      </Table.Body>
                    </Table.Content>
                  </Table.Container>
                </div>
              </Async.Success>
            </Async.Root>
          </DashboardContainer.Content>
        </DashboardContainer>
        <AnimatePresence>
          {commentPane.isOpen && (
            <SliderAnimation className="t-h-full t-shrink-0 t-grow-0 t-sticky t-top-0 t-border-0 t-border-l t-border-solid t-border-neutral-0 t-overflow-y-auto t-p-4 t-pt-0 sm:t-fixed sm:t-inset-0 sm:t-z-modal sm:t-bg-surface sm:!t-w-full">
              <CommentsSlider
                setActiveComment={setActiveComment}
                activeComment={openComment}
                onClose={commentPane.toggle}
                commentTypes={[COMMENT_TYPE.VENDOR_SUMMARY_COMMENT]}
              />
            </SliderAnimation>
          )}
        </AnimatePresence>
      </div>
      <TableSelectionControls
        deselectRows={() => setSelectedRows({})}
        allSelectedRows={selectedRows}
        hasWritePermission={hasWritePermission}
      />
      <TransactionSlider />
    </>
  );
};
